import { useState, useContext, useEffect } from "react";
import classNames from "classnames";

import { CommentPopover } from "containers/admin/clients/touchpoint/components/comments/comment-popover/comment-popover";
import { CommentPopoverButtonProps } from "containers/admin/clients/touchpoint/components/comments/comment-popover-button/comment-popover-button";
import CommentDiv from "containers/admin/clients/touchpoint/components/comments/comment-div/comment-div";

import CommentContext from "contexts/comment-context";

import { BEE_COMMENT_WRAPPER_CLASSNAME } from "utilities/constants";

import { Comment } from "types/comments";

export interface CommentableComponentProps {
  moduleId: string;
  comment: Comment | undefined;
  commentTriggerProps?: CommentPopoverButtonProps;
}

const CommentableModule = ({
  moduleId,
  comment,
  commentTriggerProps,
}: CommentableComponentProps) => {
  const {
    openCommentAnnotationId,
    setOpenCommentAnnotationId,
    newCommentModuleId,
    setNewCommentModuleId,
    commentingDisabled,
  } = useContext(CommentContext);

  const [isCommentPopoverOpen, setisCommentPopoverOpen] = useState(false);
  const [isModuleHovered, setIsModuleHovered] = useState(false);

  /* A user is interacting with this module.
   * It's hovered or the comment box is open. */
  const [isModuleActive, setIsModuleActive] = useState(false);

  // useEffect(() => {
  //   // Focus on comment input comment is expanded
  //   isCommentInputExpanded && commentInputRef.current?.focus();
  // }, [isCommentInputExpanded]);

  function closeCommentPopover() {
    if (openCommentAnnotationId) {
      setOpenCommentAnnotationId(undefined);
    }
    if (newCommentModuleId) {
      setNewCommentModuleId(undefined);
    }
  }

  function toggleCommentPopover() {
    if (openCommentAnnotationId || newCommentModuleId) {
      closeCommentPopover();
      return;
    }
    /** Setting these context values will trigger `setIsCommentOpen` via useEffect() */
    if (comment?.annotationId) {
      return setOpenCommentAnnotationId(comment.annotationId);
    }
    if (commentingDisabled) {
      return;
    }
    return setNewCommentModuleId(moduleId);
  }

  /** If `isCommentPopoverOpen`, Chakra Popover's `onClose()` will be used. */
  function handleModuleClick() {
    if (isCommentPopoverOpen) {
      return;
    }
    if (!comment && commentingDisabled) {
      return;
    }
    toggleCommentPopover();
  }

  useEffect(
    /** `CommentPopover` will show or hide based on the values in `CommentContext` */
    function handleCommentPopoverVisibility() {
      /** Modules with comments are managed here. */
      if (comment) {
        // A brand new comment has just been born.
        if (newCommentModuleId) {
          setOpenCommentAnnotationId(newCommentModuleId);
        }
        return setisCommentPopoverOpen(openCommentAnnotationId === comment?.annotationId);
      }

      /** Modules that don't yet have comments are managed here. */
      setisCommentPopoverOpen(newCommentModuleId === moduleId);
    },
    [comment, moduleId, newCommentModuleId, openCommentAnnotationId, setOpenCommentAnnotationId],
  );

  useEffect(() => {
    /**
     * The user is interacting with this module. Use `isModuleActive` to connect any styles or behaviors to this moment. */
    setIsModuleActive(isCommentPopoverOpen || isModuleHovered);
  }, [isModuleHovered, isCommentPopoverOpen]);

  if (commentingDisabled && !comment) {
    return null;
  }

  const wrapperClassName = classNames(
    BEE_COMMENT_WRAPPER_CLASSNAME,
    "absolute top-0 left-0 w-full h-full outline outline-2",
    isModuleActive ? "outline-blue-500" : "outline-transparent",
    isCommentPopoverOpen ? "pointer-events-none" : "pointer-events-all",
  );
  return (
    <CommentDiv
      className={wrapperClassName}
      onClick={handleModuleClick}
      onMouseOver={() => setIsModuleHovered(true)}
      onMouseLeave={() => setIsModuleHovered(false)}>
      <CommentPopover
        moduleId={moduleId}
        comment={comment}
        isModuleHovered={isModuleHovered}
        isCommentPopoverOpen={isCommentPopoverOpen}
        closeCommentPopover={closeCommentPopover}
        toggleCommentPopover={toggleCommentPopover}
        commentTriggerProps={{
          ...commentTriggerProps,
        }}
      />
    </CommentDiv>
  );
};

export default CommentableModule;
